import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import authRoutes from './modules/auth'
// import adminRoutes from './modules/admin'
import courseStudyRoutes from './modules/courseStudy'
import profileRoutes from './modules/profile'
import exercisesRoutes from './modules/exercises'
import degreeApplicationRoutes from './modules/degreeApplication'
import learningProgressRoutes from './modules/learningProgress'
import { isUserLoggedIn } from '@/utils/auth'
import { paymentsGetTuition, getUserInfo } from '@/api/profiles'
import config from '@/config'
import { setUserInfo, setUserShowName, setSession } from '@/utils/storage'
// QuickTracking 标准版
import { sendPageView } from '@/utils/quickTracking'

// 根据域名获取学校名称
const getSchoolNameByDomain = () => {
  const hostname = window.location.hostname
  const domainSchoolMap: Record<string, string> = {
    'web.jxjy.ccucm.tdxl-edu.cn': '长春中医药大学',
    'web.cce.cqnu.tdxl-edu.cn': '重庆师范大学',
    'web.cj.cqut.tdxl-edu.cn': '重庆理工大学',
    'web-demo.ykumda.com': '演示平台',
    'web.jjy.sntcm.tdxl-edu.cn':'陕西中医药大学',
    'web.jlnu.tdxl-edu.cn': '吉林师范大学',
    'web.eed.swupl.tdxl-edu.cn': '西南政法大学',
    'web.zjxy.nwupl.tdxl-edu.cn': '西北政法大学',
    'web.jxjyxy.hnust.tdxl-edu.cn': '湖南科技大学',
    'web.yjy.henu.tdxl-edu.cn': '河南大学',
    'web.jxjy.blcu.tdxl-edu.cn': '北京语言大学',
    'tdxl.hbucm.edu.cn': '湖北中医药大学',
    'web.jjy.hbucm.tdxl-edu.cn': '湖北中医药大学',
    'web.jxjy.wtu.tdxl-edu.cn': '武汉纺织大学',
    'web.gdou.scnu.tdxl-edu.cn': '华南师范大学',
     'web.jx.sust.tdxl-edu.cn': '陕西科技大学',
  }
  
  // 根据域名获取学校名称，如果域名不在映射中则使用默认值
  return domainSchoolMap[hostname] || '重庆理工大学'
}

// 检测是否是 H5 环境（移动端）
const isH5Environment = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  
  const userAgent = navigator.userAgent.toLowerCase()
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i
  
  // 检测移动设备
  const isMobile = mobileRegex.test(userAgent)
  
  // 检测屏幕宽度（移动端通常小于 768px）
  const isSmallScreen = window.innerWidth <= 768
  
  return isMobile || isSmallScreen
}

// import studentPortrait from './modules/studentPortrait'
// 页面路由
const routes: RouteRecordRaw[] = [
  //主页路由
  {
    path: '/',
    redirect: '/courses',
    meta: {
      title: '课程中心',
      requiresAuth: true, // 需要登录验证
    },
  },
  // 认证相关路由
  ...authRoutes,
  // 管理后台路由
  // adminRoutes,
  // studentPortrait, // 已移到 profile 子路由中
  courseStudyRoutes,
  profileRoutes,
  exercisesRoutes,
  degreeApplicationRoutes,
  learningProgressRoutes,
  // IM测试页面
  {
    path: '/test/im-test',
    name: 'IMTest',
    component: () => import('@/views/Test/IMTest.vue'),
    meta: {
      title: 'IM功能测试',
      requiresAuth: true, // 需要登录验证
    }
  },
  // 微应用页面
  {
    path: '/micro-app/:appName/:page*',
    name: 'MicroApp',
    component: () => import('@/views/MicroAppContainer.vue'),
    meta: {
      title: '子应用',
      scrollControl: false, // 添加自定义元信息
      requiresAuth: true, // 需要登录验证
    }
  },
  // 404页面
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      title: '404',
      noAuth: true // 404页面不需要登录验证
    }
  }
]

// 生产环境部分租户（如 tdxl.hbucm.edu.cn）把本站部署在 /web 子路径下，
// 若路由 base 仍为 '/'，访问 https://域名/web 会被当成未知路由而命中 404 兜底。
// 这里按当前 pathname 动态推导 base：/web 或 /web/** 时以 '/web/' 为前缀，其余环境保持默认。
const detectRouterBase = (): string => {
  const pathname = window.location.pathname
  if (pathname === '/web' || pathname.startsWith('/web/')) {
    return '/web/'
  }
  return import.meta.env.BASE_URL
}

const router = createRouter({
  history: createWebHistory(detectRouterBase()),
  routes,
  // scrollBehavior(to, from, savedPosition) {
  //   // 不再使用savedPosition，确保始终滚动到顶部
  //   // if (savedPosition) {
  //   //   return savedPosition
  //   // }
    
  //   // 处理锚点
  //   if (to.hash) {
  //     return {
  //       el: to.hash,
  //       behavior: 'smooth',
  //       // 根据导航栏高度调整偏移量
  //       top: document.querySelector('.header')?.clientHeight || 64 
  //     }
  //   }
    
  //   // 默认滚动到顶部
  //   return { top: 0, behavior: 'auto' }
  // }
})

// 全局前置守卫
router.beforeEach(async (to, from, next) => {
  console.log('路由守卫触发:', { from: from.path, to: to.path, query: to.query, href: location.href })

  // ---- 扫码活体 / 登录实人：最高优先级（必须在 /h5 强制跳转之前）----
  const pickTicket = (): string => {
    const q = to.query.ticketId || to.query.livenessTicket
    if (q) return String(Array.isArray(q) ? q[0] : q)
    try {
      const sp = new URLSearchParams(location.search || '')
      const s = sp.get('ticketId') || sp.get('livenessTicket')
      if (s) return s
      const hm = (location.hash || '').match(/[?&](?:ticketId|livenessTicket)=([^&]+)/)
      if (hm?.[1]) return decodeURIComponent(hm[1])
    } catch (_) { /* ignore */ }
    return ''
  }
  const pickLoginTicket = (): string => {
    const q = to.query.loginTicket
    if (q) return String(Array.isArray(q) ? q[0] : q)
    try {
      const sp = new URLSearchParams(location.search || '')
      const s = sp.get('loginTicket')
      if (s) return s
      const hm = (location.hash || '').match(/[?&]loginTicket=([^&]+)/)
      if (hm?.[1]) return decodeURIComponent(hm[1])
    } catch (_) { /* ignore */ }
    return ''
  }
  const pickSearchParam = (key: string): string => {
    const q = to.query[key]
    if (q != null && String(Array.isArray(q) ? q[0] : q)) {
      return String(Array.isArray(q) ? q[0] : q)
    }
    try {
      const sp = new URLSearchParams(location.search || '')
      const s = sp.get(key)
      if (s) return s
      const hm = (location.hash || '').match(new RegExp(`[?&]${key}=([^&]+)`))
      if (hm?.[1]) return decodeURIComponent(hm[1])
    } catch (_) { /* ignore */ }
    return ''
  }
  const buildLoginRpQuery = (loginTicketVal: string) => {
    const query: Record<string, string> = { loginTicket: loginTicketVal }
    const fromPc = pickSearchParam('fromPc')
    const from = pickSearchParam('from')
    if (fromPc) query.fromPc = fromPc
    if (from) query.from = from
    return query
  }
  const ticket = pickTicket()
  const loginTicket = pickLoginTicket()
  const hrefIsLiveness = /agreement-liveness/i.test(location.href || '')
  const hrefIsLoginRp = /login-realperson/i.test(location.href || '')
  const pathIsLiveness =
    to.path === '/agreement-liveness' || to.path.startsWith('/agreement-liveness/')
  const pathIsLoginRp =
    to.path === '/login-realperson' || to.path.startsWith('/login-realperson/')

  if (loginTicket && !pathIsLoginRp) {
    next({ path: '/login-realperson', query: buildLoginRpQuery(loginTicket), replace: true })
    return
  }
  if (!pathIsLoginRp && hrefIsLoginRp) {
    next({
      path: '/login-realperson',
      query: loginTicket ? buildLoginRpQuery(loginTicket) : {},
      replace: true,
    })
    return
  }
  if (pathIsLoginRp) {
    document.title = '登录实人认证'
    next()
    return
  }

  if (ticket && !pathIsLiveness) {
    next({ path: '/agreement-liveness', query: { ticketId: ticket }, replace: true })
    return
  }
  // 仍带 hash 的活体链接（bootstrap 未生效时）
  if (!pathIsLiveness && hrefIsLiveness) {
    next({
      path: '/agreement-liveness',
      query: ticket ? { ticketId: ticket } : {},
      replace: true,
    })
    return
  }
  // 已在活体页：直接放行，禁止后续跳 /h5
  if (pathIsLiveness) {
    document.title = '协议实人认证'
    next()
    return
  }
  // ---- 扫码活体结束 ----

  // 机位二手机端监控页：手机扫码时 UA 命中 isH5Environment，若不在此提前放行，
  // 会被下方“H5 环境自动跳转”甩到 /h5 登录页。与 login-realperson、agreement-liveness 同处理。
  const pathIsMobileMonitor =
    to.path === '/exercises/mobile-monitor' || to.path.startsWith('/exercises/mobile-monitor/')
  if (pathIsMobileMonitor) {
    document.title = '手机端监控'
    next()
    return
  }

  // 如果访问的是 /h5 路径
  if (to.path.startsWith('/h5')) {
    // 如果是 PC 环境，跳转到首页
    if (!isH5Environment()) {
      console.log('PC 环境访问 /h5，跳转到首页')
      next({ path: '/', replace: true })
      return
    }
    // 如果是 H5 环境，阻止路由守卫，让服务器直接处理静态文件
    console.log('H5 环境访问 /h5，阻止路由守卫，由服务器直接提供静态文件')
    next(false)
    return
  }
  
  // 检测 H5 环境，如果是 H5 环境且不在 /h5 路径下，自动跳转到 /h5
  // 活体入口已在上方 return，不会进入这里
  if (isH5Environment() && !to.path.startsWith('/h5')) {
    // 二次保险：地址栏仍含活体信息时绝不跳 /h5
    if (/login-realperson|(?:[?&#]loginTicket=)/i.test(location.href)) {
      const t = pickLoginTicket()
      next({
        path: '/login-realperson',
        query: t ? buildLoginRpQuery(t) : {},
        replace: true,
      })
      return
    }
    if (/agreement-liveness|livenessTicket|(?:[?&#]ticketId=)/i.test(location.href)) {
      const t = pickTicket()
      next({
        path: '/agreement-liveness',
        query: t ? { ticketId: t } : {},
        replace: true,
      })
      return
    }
    console.log('检测到 H5 环境，自动跳转到 /h5')
    window.location.href = location.origin + '/h5'
    return
  }
  
  // 设置页面标题
  const schoolName = getSchoolNameByDomain()
  document.title = `${to.meta.title || schoolName} - ${schoolName}`
  
  // 处理路由参数中的 authToken 和 userId
  const authToken = to.query.authToken as string | undefined
  const userId = to.query.userId as string | undefined
  let hasProcessedAuthParams = false
  
  if (authToken) {
    console.log('检测到路由参数中的 authToken，存储到 EDU_WEB_TOKEN')
    localStorage.setItem(config.tokenName, authToken)
    hasProcessedAuthParams = true
  }
  
  if (userId) {
    console.log('检测到路由参数中的 userId，获取用户信息')
    try {
      const response = await getUserInfo(userId)
      // 处理返回数据格式：可能是 res.data 或直接是 res
      const userInfo = (response as any)?.data || response
      
      if (userInfo) {
        // 确保用户信息中包含 userId
        const userInfoWithUserId = {
          ...userInfo,
          userId: userId
        }
        
        // 存储用户信息到 localStorage 和 sessionStorage
        setUserInfo(userInfoWithUserId)
        setSession('userInfo', userInfoWithUserId)
        
        // 如果用户信息中有 showName 或 name，存储到 userShowName
        const showName = userInfo.showName || userInfo.name || userInfo.nickname
        if (showName) {
          setUserShowName(showName)
          console.log('用户 showName 已存储:', showName)
        }
        
        console.log('用户信息已缓存:', userInfoWithUserId)
        hasProcessedAuthParams = true
      }
    } catch (error) {
      console.error('获取用户信息失败:', error)
    }
  }
  
  // 检查路由是否需要登录验证
  const requiresAuth = to.meta.requiresAuth !== false && to.meta.noAuth !== true
  const isLoggedIn = isUserLoggedIn()
  
  console.log('认证状态检查:', { requiresAuth, isLoggedIn, toPath: to.path })
  
  // 如果路由需要登录验证但用户未登录
  if (requiresAuth && !isLoggedIn) {
    console.log('需要登录，重定向到登录页面')
    // 保存当前路径作为重定向目标
    const redirectPath = to.fullPath
    next({
      path: '/login',
      query: { redirect: encodeURIComponent(redirectPath) }
    })
    return
  }
  
  // 如果用户已登录但访问登录页面，重定向到个人中心
  if (isLoggedIn && to.path === '/login') {
    console.log('已登录用户访问登录页面，重定向')
    // 检查是否有重定向参数，如果有则跳转到目标页面
    if (to.query.redirect) {
      const redirectPath = decodeURIComponent(to.query.redirect as string)
      console.log('重定向到:', redirectPath)
      next({ path: redirectPath })
    } else {
      console.log('重定向到个人中心')
      next({ path: '/profile' })
    }
    return
  }
  
  // 如果处理了认证参数，从 URL 中移除这些敏感参数
  if (hasProcessedAuthParams) {
    const newQuery = { ...to.query }
    delete newQuery.authToken
    delete newQuery.userId
    
    // 使用 replace 方式跳转，避免在浏览器历史记录中留下敏感信息
    console.log('清理 URL 中的敏感参数')
    next({
      path: to.path,
      query: newQuery,
      replace: true
    })
    return
  }
  
  console.log('正常跳转')
  // 其他情况正常跳转
  next()
})

// 存储定时器ID，用于清除之前的定时器
let paymentTimer: ReturnType<typeof setTimeout> | null = null

// 检查是否是特定域名（需要调用缴费接口的域名）
const shouldCallPaymentApi = () => {
  const hostname = window.location.hostname
  const origin = window.location.origin
  return hostname === 'web.jjy.sntcm.tdxl-edu.cn' || origin.includes('web.jjy.sntcm.tdxl-edu.cn')
}

// 全局后置守卫 - 路由切换后处理缴费接口调用和 PV 埋点
router.afterEach((to, from) => {
  // 清除之前的定时器
  if (paymentTimer) {
    clearTimeout(paymentTimer)
    paymentTimer = null
  }

  // ==================== QuickTracking PV 埋点 ====================
  sendPageView(
    {
      page_name: (to.name as string) || to.path,
      page_title: document.title,
      is_auto: false  // 手动上报
    },
    {
      route_path: to.path,
      route_name: to.name,
      previous_page: from.name || from.path,
      query: JSON.stringify(to.query)
    }
  )
  // ============================================================

  // 检查是否需要调用缴费接口
  if (!shouldCallPaymentApi()) {
    return
  }

  // 检查用户是否已登录
  if (!isUserLoggedIn()) {
    return
  }

  // 排除登录页面
  if (to.path === '/login') {
    return
  }

  // 设置新的定时器，10秒后调用缴费接口
  paymentTimer = setTimeout(() => {
    console.log('路由切换后停留10秒，调用缴费接口:', to.path)
    paymentsGetTuition({}).catch((error: any) => {
      console.error('调用缴费接口失败:', error)
    })
    paymentTimer = null
  }, 10000) // 10秒 = 10000毫秒
})

export default router 